/**
 * ChartsSection Component
 * Displays synchronized charts for processed data
 */

import React, { useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/buttons/button';
import { RotateCcw, Info } from 'lucide-react';
import ProcessedDataChart from '../../../shared/components/charts/ProcessedDataChart';
import { WebGLChart } from '../../../shared/components/charts/WebGLChart';
import type { InfoType, BrushRange } from '../types/dataAdjustments.types';

// Feature flag for WebGL chart - set to true to use WebGL, false for uPlot
const USE_WEBGL_CHART = true;

interface ChartsSectionProps {
  processedDataFrames: Array<unknown>;
  adjustedData: InfoType[];
  sharedBrushRange: BrushRange | null;
  onSharedBrushChange: (range: BrushRange | null) => void;
}

const ChartsSection: React.FC<ChartsSectionProps> = ({
  processedDataFrames,
  adjustedData,
  sharedBrushRange,
  onSharedBrushChange
}) => {
  const { t } = useTranslation('dataAdjustments');
  const { t: tCharts } = useTranslation('charts');

  // Show points state for all charts
  const [showPoints, setShowPoints] = useState(false);

  // Track which charts have visible points (keyed by chart index)
  const pointsVisibilityRef = useRef<Record<number, boolean>>({});
  const [anyPointsVisible, setAnyPointsVisible] = useState(false);

  // Handle visibility change from individual charts
  const handlePointsVisibilityChange = useCallback((index: number, visible: boolean) => {
    pointsVisibilityRef.current[index] = visible;
    // Check if ANY chart has visible points
    const hasAnyVisible = Object.values(pointsVisibilityRef.current).some(v => v);
    setAnyPointsVisible(hasAnyVisible);
  }, []);

  // Reset zoom for all charts
  const handleResetAllZoom = useCallback(() => {
    onSharedBrushChange(null);
  }, [onSharedBrushChange]);

  // Toggle show points for all charts
  const handleToggleShowPoints = useCallback(() => {
    setShowPoints(prev => !prev);
  }, []);

  if (processedDataFrames.length === 0) return null;

  return (
    <div className="mt-8 border border-border rounded-md p-4">
      <h2 className="text-xl font-bold mb-4">
        {t('processedData.visualization')}
      </h2>

      {/* Shared controls - displayed once for all charts */}
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetAllZoom}
        >
          <RotateCcw className="h-4 w-4 mr-1.5" />
          {tCharts("dataChart.resetZoom", "Reset Zoom")}
        </Button>

        {/* Show points toggle */}
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <span className="whitespace-nowrap">{tCharts("dataChart.showPoints", "Show points")}</span>
          <button
            type="button"
            role="switch"
            aria-checked={showPoints}
            onClick={handleToggleShowPoints}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
              showPoints ? "bg-primary" : "bg-muted"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                showPoints ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
        </label>

        {/* Hint when points enabled but not visible due to zoom level */}
        {showPoints && !anyPointsVisible && (
          <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
            <Info className="h-3.5 w-3.5" />
            {tCharts("dataChart.zoomToShowPoints", "Zoom in to see data points")}
          </span>
        )}
      </div>

      <div className="space-y-4">
        {processedDataFrames.map((dataPoints, index) => {
          // Validate data structure
          if (!dataPoints || !Array.isArray(dataPoints) || dataPoints.length === 0) {
            console.warn(`Empty or invalid data for frame ${index}`);
            return null;
          }
          
          const seriesName = adjustedData[index]?.["Name der Messreihe"];
          const fileName = adjustedData[index]?.["Name der Datei"];
          
          if (!seriesName || !fileName) {
            console.warn(`Missing series name or file name for index ${index}`, {
              seriesName, 
              fileName, 
              adjustedDataEntry: adjustedData[index]
            });
            return null;
          }
          
          const uniqueKey = `${fileName}-${seriesName}`;
          
          // Check if data points have required structure
          const hasValidStructure = dataPoints.some(point => 
            point && 
            typeof point === 'object' && 
            'UTC' in point && 
            (seriesName in point || 'value' in point)
          );
          
          if (!hasValidStructure) {
            console.warn(`Invalid data structure for ${fileName} - ${seriesName}`);
            return null;
          }
          
          // Select chart component based on feature flag
          const ChartComponent = USE_WEBGL_CHART ? WebGLChart : ProcessedDataChart;

          return (
            <div key={uniqueKey}>
              <ChartComponent
                data={dataPoints}
                seriesName={seriesName}
                filename={fileName}
                sharedBrushRange={sharedBrushRange}
                onSharedBrushChange={onSharedBrushChange}
                hideControls={true}
                showPoints={showPoints}
                onPointsVisibilityChange={(visible: boolean) => handlePointsVisibilityChange(index, visible)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChartsSection;
